import { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Title,
  Text,
  Button,
  Stack,
  Paper,
  Group,
  Progress,
  Loader,
  Center,
  Box,
  ActionIcon,
} from '@mantine/core';
import { IconArrowLeft, IconCheck, IconBook } from '@tabler/icons-react';
import { useStory } from '@/features/story/hooks/useStory';
import { useSubmitQuiz, QuizAnswer } from '@/features/quiz/hooks/useQuiz';
import { StoryReferenceDrawer } from '@/features/quiz/components/StoryReferenceDrawer';
import { usePlatform } from '@/hooks/usePlatform';

/**
 * Mobile Quiz Page
 *
 * Touch-optimized quiz interface:
 * - Large answer buttons
 * - Full-screen layout
 * - Swipe-friendly navigation
 * - Clear visual feedback
 * - iOS safe area support
 */
export function MobileQuizPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { isIOS } = usePlatform();
  const sessionId = location.state?.sessionId || '';
  const readingTime = location.state?.readingTime || 0;

  const { data: story, isLoading } = useStory(id!);
  const submitQuiz = useSubmitQuiz();

  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [questionTimes, setQuestionTimes] = useState<Record<number, number>>({});
  const [questionStartTime, setQuestionStartTime] = useState<number>(Date.now());
  const [quizStartedAt, setQuizStartedAt] = useState<Date | null>(null);
  const [storyDrawerOpen, setStoryDrawerOpen] = useState(false);

  // Set quiz start time once
  useEffect(() => {
    if (!quizStartedAt) {
      setQuizStartedAt(new Date());
    }
  }, [quizStartedAt]);

  // Reset question start time when question changes
  useEffect(() => {
    setQuestionStartTime(Date.now());
  }, [currentQuestion]);

  if (isLoading || !story) {
    return (
      <Center h="100vh">
        <Loader color="teal" size="lg" />
      </Center>
    );
  }

  const questions = story.questions || [];

  // If no questions, show message
  if (questions.length === 0) {
    return (
      <Center h="100vh" p="md">
        <Stack gap="md" align="center">
          <Title order={3}>No Questions Available</Title>
          <Text c="dimmed" ta="center">
            This story doesn't have any quiz questions yet.
          </Text>
          <Button variant="light" onClick={() => navigate('/')}>
            Go Home
          </Button>
        </Stack>
      </Center>
    );
  }

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  const handleAnswer = (value: string) => {
    setAnswers((prev) => ({ ...prev, [currentQuestion]: value }));

    // Haptic feedback
    if (navigator.vibrate) {
      navigator.vibrate(30);
    }
  };

  const handleNext = () => {
    // Save time spent on current question
    const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
    setQuestionTimes((prev) => ({ ...prev, [currentQuestion]: timeSpent }));

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    // Save time spent on current question
    const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
    setQuestionTimes((prev) => ({ ...prev, [currentQuestion]: timeSpent }));

    if (currentQuestion > 0) {
      setCurrentQuestion((prev) => prev - 1);
    }
  };

  const handleSubmit = () => {
    // Save time spent on final question
    const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
    const finalQuestionTimes = { ...questionTimes, [currentQuestion]: timeSpent };

    const quizAnswers: QuizAnswer[] = questions.map((q, index) => ({
      question_id: q.id,
      selected_answer: answers[index] || '',
      time_spent_seconds: finalQuestionTimes[index] || 0,
    }));

    submitQuiz.mutate({
      sessionId,
      answers: quizAnswers,
      readingTime,
      quizStartedAt: quizStartedAt!,
      questionTimes: finalQuestionTimes,
      storyId: id,
    });
  };

  const handleBack = () => {
    navigate(`/story/${id}`);
  };

  const allAnswered = questions.every((_, index) => answers[index]);
  const isLastQuestion = currentQuestion === questions.length - 1;

  return (
    <Box
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f8f9fa',
      }}
    >
      {/* Top Bar */}
      <Box
        style={{
          backgroundColor: 'white',
          borderBottom: '1px solid #e9ecef',
          paddingTop: isIOS ? 'env(safe-area-inset-top)' : 0,
        }}
      >
        <Group justify="space-between" p="md" gap="xs">
          <ActionIcon variant="subtle" color="gray" size="lg" onClick={handleBack}>
            <IconArrowLeft size={20} />
          </ActionIcon>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" fw={500} lineClamp={1}>
              {story.title}
            </Text>
            <Text size="xs" c="dimmed">
              Question {currentQuestion + 1} of {questions.length}
            </Text>
          </Box>
          <ActionIcon
            variant="subtle"
            color="teal"
            size="lg"
            onClick={() => setStoryDrawerOpen(true)}
            aria-label="View story"
          >
            <IconBook size={20} />
          </ActionIcon>
        </Group>
        <Progress value={progress} size="xs" color="teal" />
      </Box>

      {/* Question Content */}
      <Box
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '16px',
        }}
      >
        <Stack gap="md">
          {/* Question Text */}
          <Paper p="lg" withBorder>
            <Text
              fw={600}
              size="lg"
              style={{
                fontFamily: "'Comic Neue', 'Comic Sans MS', cursive",
                lineHeight: 1.6,
              }}
            >
              {currentQ.question_text}
            </Text>
          </Paper>

          {/* Answer Options */}
          <Stack gap="sm">
            {(Array.isArray(currentQ.options) ? currentQ.options : Object.values(currentQ.options)).map(
              (option: string, index: number) => {
                const letterValue = String.fromCharCode(65 + index); // 65 is 'A'
                const isSelected = answers[currentQuestion] === letterValue;

                return (
                  <Paper
                    key={index}
                    p="md"
                    withBorder
                    onClick={() => handleAnswer(letterValue)}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: isSelected ? '#e6fcf5' : 'white',
                      borderColor: isSelected ? '#20c997' : '#dee2e6',
                      borderWidth: isSelected ? '2px' : '1px',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Group gap="md" wrap="nowrap">
                      <Box
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '50%',
                          backgroundColor: isSelected ? '#20c997' : '#f1f3f5',
                          color: isSelected ? 'white' : '#495057',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 600,
                          fontSize: '18px',
                          flexShrink: 0,
                        }}
                      >
                        {isSelected ? <IconCheck size={20} /> : letterValue}
                      </Box>
                      <Text
                        style={{
                          flex: 1,
                          fontFamily: "'Comic Neue', 'Comic Sans MS', cursive",
                          fontSize: '16px',
                          lineHeight: 1.5,
                        }}
                      >
                        {option}
                      </Text>
                    </Group>
                  </Paper>
                );
              }
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Bottom Navigation */}
      <Box
        style={{
          backgroundColor: 'white',
          borderTop: '1px solid #e9ecef',
          paddingBottom: isIOS ? 'calc(16px + env(safe-area-inset-bottom))' : '16px',
        }}
      >
        <Group p="md" justify="space-between">
          <Button
            variant="subtle"
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
            size="md"
          >
            Previous
          </Button>

          {isLastQuestion ? (
            <Button
              variant="filled"
              color="teal"
              onClick={handleSubmit}
              disabled={!allAnswered}
              loading={submitQuiz.isPending}
              size="lg"
              style={{ flex: 1, maxWidth: '200px' }}
            >
              Submit Quiz
            </Button>
          ) : (
            <Button
              variant="light"
              onClick={handleNext}
              disabled={!answers[currentQuestion]}
              size="lg"
              style={{ flex: 1, maxWidth: '200px' }}
            >
              Next Question
            </Button>
          )}
        </Group>
      </Box>

      <StoryReferenceDrawer
        opened={storyDrawerOpen}
        onClose={() => setStoryDrawerOpen(false)}
        storyTitle={story.title}
        passage={story.passage}
        question={currentQ.question_text || currentQ.question}
        optionTexts={
          Array.isArray(currentQ.options)
            ? currentQ.options
            : Object.values(currentQ.options)
        }
      />
    </Box>
  );
}
